import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FocusHeader } from '../components/FocusHeader';
import { StepIndicator } from '../components/StepIndicator';
import { StepProgressIndicator, ProgressStep } from '../components/StepProgressIndicator';
import { useSession } from '../contexts/SessionContext';
import { supabase } from '../lib/supabase';
import { getWizardSteps } from '../lib/wizardSteps';
import { WIZARD_EXIT_ROUTE, WIZARD_LEAVE_HINT, WIZARD_PATHS, sessionIdFromLocation, wizardUrl } from '../lib/wizardRoutes';
import { waitForSessionUpdate } from '../lib/navigationHelpers';
import { logGenerationError } from '../lib/errorLogger';

const INTRO_GENERATION_TIMEOUT = 45000;
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY = 2000;

interface FetchWithTimeoutOptions {
  timeout: number;
  signal?: AbortSignal;
}

async function fetchWithTimeout(
  url: string,
  options: RequestInit & FetchWithTimeoutOptions
): Promise<Response> {
  const { timeout, signal, ...fetchOptions } = options;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Timeout: Het duurt te lang, probeer opnieuw');
    }
    throw error;
  }
}

async function sleep(ms: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export function IntroGenerate() {
  const navigate = useNavigate();
  const location = useLocation();
  const { session, user, currentIdea } = useSession();
  const sessionId = sessionIdFromLocation(location);
  const [error, setError] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboardSession, setDashboardSession] = useState<any>(null);
  const [dashboardUser, setDashboardUser] = useState<any>(null);
  const [dashboardIdea, setDashboardIdea] = useState<any>(null);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const isMountedRef = useRef(true);
  const hasStartedRef = useRef(false);
  const [progressSteps, setProgressSteps] = useState<ProgressStep[]>([
    { label: 'Gekozen titel laden', status: 'pending' },
    { label: 'Prompt voorbereiden', status: 'pending' },
    { label: "AI intro's genereren", status: 'pending' },
    { label: "Intro's opslaan", status: 'pending' },
  ]);

  const updateStepStatus = (stepIndex: number, status: 'pending' | 'in_progress' | 'completed' | 'error') => {
    setProgressSteps(prev => prev.map((step, idx) =>
      idx === stepIndex ? { ...step, status } : step
    ));
  };

  useEffect(() => {
    if (sessionId) {
      loadDashboardSession(sessionId);
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  const loadDashboardSession = async (sid: string) => {
    try {
      setLoading(true);

      const { data: sessionData, error: sessionError } = await supabase
        .from('sessions')
        .select('*')
        .eq('id', sid)
        .maybeSingle();

      if (sessionError || !sessionData) {
        console.error('Error loading session:', sessionError);
        setLoading(false);
        return;
      }

      const { data: userData } = await supabase
        .from('users')
        .select('*')
        .eq('id', sessionData.user_id)
        .maybeSingle();

      const { data: ideaData } = await supabase
        .from('blog_ideas')
        .select('*')
        .eq('id', sessionData.approved_idea_id)
        .maybeSingle();

      setDashboardSession(sessionData);
      setDashboardUser(userData);
      setDashboardIdea(ideaData);
      setLoading(false);
    } catch (err) {
      console.error('Error loading dashboard session:', err);
      setLoading(false);
    }
  };

  const activeSession = dashboardSession || session;
  const activeUser = dashboardUser || user;
  const activeIdea = dashboardIdea || currentIdea;

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (loading) return;
    if (error) return;
    if (!activeSession || !activeUser) return;
    if (isGenerating) return;
    if (hasStartedRef.current) return;
    hasStartedRef.current = true;

    const generateIntros = async () => {
      setIsGenerating(true);
      try {
        console.log('Checking for existing intros...');
        updateStepStatus(0, 'in_progress');

        const { data: sessionData, error: sessionError } = await supabase
          .from('sessions')
          .select('intros, article_content, selected_title')
          .eq('id', activeSession.id)
          .maybeSingle();

        if (!sessionData?.article_content) {
          console.error('No article content found in session');
          updateStepStatus(0, 'error');
          throw new Error('Er is geen artikel beschikbaar');
        }

        if (!sessionData?.selected_title) {
          console.error('No title selected');
          updateStepStatus(0, 'error');
          throw new Error('Er is geen titel geselecteerd');
        }

        if (sessionError) {
          console.error('Error fetching session:', sessionError);
        }

        if (sessionData?.intros && Array.isArray(sessionData.intros) && sessionData.intros.length > 0) {
          console.log('Using existing intros:', sessionData.intros);
          if (!isMountedRef.current) return;
          navigate(wizardUrl(WIZARD_PATHS.selectIntro, activeSession.id), { replace: true });
          return;
        }

        const inflightKey = `gen_inflight_intros_${activeSession.id}`;
        const inflightSince = Number(sessionStorage.getItem(inflightKey) || 0);
        if (inflightSince && Date.now() - inflightSince < 3 * 60 * 1000) {
          // Een eerdere aanroep loopt waarschijnlijk nog (terug, Sluiten of refresh tijdens het schrijven):
          // wacht op het resultaat in plaats van opnieuw te betalen.
          console.log('[IntroGenerate] Generation already in flight, waiting for result...');
          updateStepStatus(0, 'completed');
          updateStepStatus(1, 'completed');
          updateStepStatus(2, 'in_progress');
          const arrived = await waitForSessionUpdate(activeSession.id, 'intros', 45, 2000);
          if (arrived) {
            sessionStorage.removeItem(inflightKey);
            if (!isMountedRef.current) return;
            navigate(wizardUrl(WIZARD_PATHS.selectIntro, activeSession.id), { replace: true });
            return;
          }
          sessionStorage.removeItem(inflightKey);
        }
        sessionStorage.setItem(inflightKey, String(Date.now()));

        updateStepStatus(0, 'completed');
        updateStepStatus(1, 'in_progress');

        console.log('Generating new intros...');
        console.log('Article content length:', sessionData.article_content.length);
        console.log('Selected title:', sessionData.selected_title);

        const { data: { session } } = await supabase.auth.getSession();
        const authToken = session?.access_token || (import.meta.env.VITE_SUPABASE_ANON_KEY);

        let lastError: Error | null = null;
        let introsData: any = null;
        let creditError = false;

        for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt++) {
          try {
            setRetryAttempt(attempt);
            console.log(`[IntroGenerate] Attempt ${attempt}/${MAX_RETRY_ATTEMPTS}`);

            const introsResponse = await fetchWithTimeout(
              `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/openai-generate`,
              {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${authToken}`,
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  type: 'intros',
                  session_id: activeSession.id,
                  user_id: activeUser.id,
                  data: {
                    idea_title: activeIdea?.idea_title || '',
                    idea_content: activeIdea?.idea_content || '',
                    article_content: sessionData.article_content,
                    selected_title: sessionData.selected_title,
                  },
                }),
                timeout: INTRO_GENERATION_TIMEOUT,
              }
            );

            introsData = await introsResponse.json();

            if (!introsResponse.ok) {
              console.error(`[IntroGenerate] API error on attempt ${attempt}:`, introsData);
              const isInsufficientCredits = introsResponse.status === 402 || introsData.error === 'INSUFFICIENT_CREDITS';
              if (isInsufficientCredits) {
                throw Object.assign(new Error("Onvoldoende credits om intro's te genereren. Koop extra credits om door te gaan."), { isCreditError: true });
              }
              throw new Error(introsData.error || 'Failed to generate intros');
            }

            console.log(`[IntroGenerate] Success on attempt ${attempt}`);
            lastError = null;
            break;
          } catch (error) {
            lastError = error as Error;
            console.error(`[IntroGenerate] Error on attempt ${attempt}:`, error);

            if ((error as any).isCreditError) {
              creditError = true;
              break;
            }

            const errorMessage = error instanceof Error ? error.message : String(error);
            const isTimeout = errorMessage.includes('Timeout');
            const isNetwork = errorMessage.includes('fetch') || errorMessage.includes('network');

            await logGenerationError({
              generationType: 'blog_intro',
              errorCategory: isTimeout ? 'TIMEOUT' : isNetwork ? 'NETWORK_ERROR' : 'API_ERROR',
              errorMessage: errorMessage,
              technicalError: error instanceof Error ? error.stack : undefined,
              retryAttempt: attempt - 1,
              blogId: activeSession.id,
              sessionId: activeSession.id,
              additionalContext: {
                service: 'openai',
                apiType: 'intro_generation',
                timeout: INTRO_GENERATION_TIMEOUT,
                lastError: errorMessage,
                maxRetries: MAX_RETRY_ATTEMPTS,
                attemptNumber: attempt,
              },
            });

            if (attempt < MAX_RETRY_ATTEMPTS) {
              console.log(`[IntroGenerate] Waiting ${RETRY_DELAY}ms before retry...`);
              await sleep(RETRY_DELAY);
            }
          }
        }

        if (lastError) {
          updateStepStatus(2, 'error');

          await logGenerationError({
            generationType: 'blog_intro',
            errorCategory: 'TIMEOUT',
            errorMessage: `Na ${MAX_RETRY_ATTEMPTS} pogingen niet gelukt`,
            technicalError: lastError.stack,
            retryAttempt: MAX_RETRY_ATTEMPTS,
            blogId: activeSession.id,
            sessionId: activeSession.id,
            additionalContext: {
              service: 'openai',
              apiType: 'intro_generation',
              timeout: INTRO_GENERATION_TIMEOUT,
              lastError: lastError.message,
              maxRetries: MAX_RETRY_ATTEMPTS,
            },
          });

          if (creditError) throw lastError;

          throw new Error(
            `Na ${MAX_RETRY_ATTEMPTS} pogingen niet gelukt. Probeer het later opnieuw of neem contact op met support.`
          );
        }

        if (!introsData) {
          updateStepStatus(2, 'error');
          throw new Error('Geen resultaat ontvangen van AI');
        }

        setRetryAttempt(0);
        updateStepStatus(1, 'completed');
        updateStepStatus(2, 'in_progress');

        console.log('Intros API response:', introsData);

        let intros = [];

        // NEW: Parse data property (current edge function format)
        if (introsData.data) {
          try {
            const parsed = typeof introsData.data === 'string'
              ? JSON.parse(introsData.data)
              : introsData.data;
            if (Array.isArray(parsed)) {
              intros = parsed;
            }
          } catch (e) {
            console.error('Failed to parse intros data:', e);
          }
        }

        // FALLBACK: Old formats (backwards compatible)
        if (intros.length === 0) {
          if (Array.isArray(introsData.result)) {
            intros = introsData.result;
          } else if (introsData.result?.intros && Array.isArray(introsData.result.intros)) {
            intros = introsData.result.intros;
          }
        }

        if (intros.length === 0) {
          console.error('No valid intros received. Full response:', JSON.stringify(introsData, null, 2));
          updateStepStatus(2, 'error');
          throw new Error('Geen intros ontvangen van de API');
        }

        updateStepStatus(2, 'completed');
        updateStepStatus(3, 'in_progress');

        const { error: updateError } = await supabase
          .from('sessions')
          .update({ intros: intros })
          .eq('id', activeSession.id);

        if (updateError) {
          console.error('Error saving intros:', updateError);
          updateStepStatus(3, 'error');
          throw new Error('Fout bij opslaan van intros');
        }

        sessionStorage.removeItem(inflightKey);

        console.log('Verifying intros were saved to database...');
        const verified = await waitForSessionUpdate(activeSession.id, 'intros', 15, 300);

        if (!verified) {
          console.warn('Could not verify intros in database within timeout, but continuing anyway');
          console.log('Intros that were returned:', intros);
        }

        updateStepStatus(3, 'completed');
        console.log('Intros verified in database!');

        await new Promise(resolve => setTimeout(resolve, 500));

        console.log('Navigating with intros:', intros);
        if (!isMountedRef.current) return;
        navigate(wizardUrl(WIZARD_PATHS.selectIntro, activeSession.id), { replace: true });
        setIsGenerating(false);
      } catch (err) {
        sessionStorage.removeItem(`gen_inflight_intros_${activeSession.id}`);
        console.error('Error generating intros:', err);

        await logGenerationError({
          generationType: 'blog_intro',
          errorCategory: 'UNKNOWN',
          errorMessage: err instanceof Error ? err.message : 'Unknown error',
          technicalError: err instanceof Error ? err.stack : undefined,
          retryAttempt: retryAttempt,
          blogId: activeSession?.id,
          sessionId: activeSession?.id,
          additionalContext: {
            service: 'openai',
            apiType: 'intro_generation',
            timeout: INTRO_GENERATION_TIMEOUT,
            lastError: err instanceof Error ? err.message : String(err),
          },
        });

        setError(
          `Er ging iets mis bij het genereren van intro's: ${err instanceof Error ? err.message : 'Unknown error'}`
        );
        setIsGenerating(false);
        setRetryAttempt(0);
      }
    };

    generateIntros();
  }, [activeSession, activeUser, activeIdea, loading, isGenerating, error]);

  const steps = getWizardSteps(activeSession, 5);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <FocusHeader
          title="Intro's genereren"
          onClose={() => navigate(WIZARD_EXIT_ROUTE, { replace: true })}
          hideSaveStatus
        />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-center min-h-[50vh]">
            <div className="text-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E94E87] mx-auto"></div>
              <p className="text-gray-600">Laden...</p>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <FocusHeader
          title="Intro's genereren"
          onClose={() => navigate(WIZARD_EXIT_ROUTE, { replace: true })}
          hideSaveStatus
        />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="card bg-red-50 border-red-200">
            <p className="text-red-800">{error}</p>
            <button
              onClick={() => navigate(wizardUrl(WIZARD_PATHS.selectTitle, activeSession?.id), { replace: true })}
              className="btn-secondary mt-4"
            >
              Terug naar titels
            </button>
          </div>
        </main>
      </div>
    );
  }

  if (!loading && !activeSession) {
    return (
      <div className="min-h-screen bg-[#F2F2EF]">
        <FocusHeader title="Intro's genereren" onClose={() => navigate(WIZARD_EXIT_ROUTE)} hideSaveStatus />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="card bg-red-50 border-red-200">
            <p className="text-red-800">Geen sessie gevonden. Open de blog opnieuw vanuit je concepten.</p>
            <button onClick={() => navigate(WIZARD_EXIT_ROUTE)} className="btn-secondary mt-4">Naar Mijn Blogs</button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F2F2EF]">
      <FocusHeader
        title="Intro's genereren"
        onClose={() => navigate(WIZARD_EXIT_ROUTE, { replace: true })}
        hideSaveStatus
      />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <StepIndicator steps={steps} interactive={false} />
        <div className="space-y-6 mt-6">
          <div className="space-y-2">
            <h1 className="text-2xl md:text-3xl font-bold text-[#231F20]">
              We maken intro's voor je...
            </h1>
            <p className="text-gray-600">Een moment geduld.</p>
            <p className="text-sm text-gray-500">{WIZARD_LEAVE_HINT}</p>
          </div>
          <StepProgressIndicator steps={progressSteps} />
        </div>
      </main>
    </div>
  );
}
